import { memo, useCallback } from "react";
import { ActivityIndicator, Pressable } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { LegacySnapshotImage } from "@src/features/cameras/components/LegacySnapshotImage";
import { getCameraKeyString } from "@src/utils/key-parser";
import type { CameraSiteGroup } from "@src/features/cameras/utils/camera-group";

interface SiteCardProps {
  group: CameraSiteGroup;
  isSelected?: boolean;
  onPress: (siteId: string) => void;
}

export const SiteCard = memo(({ group, isSelected, onPress }: SiteCardProps) => {
  const previewCameras = group.cameras.slice(0, 4);
  const { theme } = useUnistyles();
  const handlePress = useCallback(() => {
    onPress(group.siteId);
  }, [onPress, group.siteId]);

  return (
    <Pressable
      onPress={handlePress}
      style={[
        styles.siteCard,
        isSelected && {
          borderColor: theme.colors.interactive.secondary.default_border,
          backgroundColor: theme.colors.surface2.background,
        },
      ]}
    >
      <View style={styles.siteThumbnails}>
        {previewCameras.map((camera) => (
          <View key={getCameraKeyString(camera)} style={styles.siteThumbnail}>
            {camera ?
              <LegacySnapshotImage
                cameraKey={getCameraKeyString(camera)}
                thumbnailUrl={camera.snapshot?.url}
                capturedAt={camera.snapshot?.capturedAt}
                contentFit="cover"
                style={styles.siteThumbnailImage}
              />
            : <View style={styles.placeholder}>
                <ActivityIndicator size="small" color={theme.colors.textMuted} />
              </View>
            }
          </View>
        ))}
      </View>
      <View flex={1} justifyContent="center" gap="md" px="3xl">
        <Text textStyle="md/medium" color="text">
          {group.siteName}
        </Text>
        {(group.siteSubtitle || group.siteId) && (
          <>
            <View>
              <View height={1} bg="borderCard" />
            </View>
            <Text textStyle="xs/regular" color="textMuted">
              {group.siteSubtitle || group.siteId}
            </Text>
          </>
        )}
      </View>
    </Pressable>
  );
});

const styles = StyleSheet.create((theme) => ({
  siteCard: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xs,
    padding: theme.spacing.xl,
    overflow: "hidden",
    backgroundColor: theme.colors.surfaceCard,
  },
  siteThumbnails: {
    width: 120,
    height: 90,
    flexDirection: "row",
    flexWrap: "wrap",
    borderRadius: theme.spacing.xxs,
    overflow: "hidden",
    backgroundColor: theme.colors.borderCard,
  },
  siteThumbnail: {
    width: "50%",
    height: "50%",
    overflow: "hidden",
    backgroundColor: theme.colors.borderCard,
  },
  siteThumbnailImage: {
    width: "100%",
    height: "100%",
  },
  placeholder: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors.borderCard,
  },
}));
